/**
 * Metro Store — Zustand store for selected metro area.
 *
 * Persisted to localStorage. Selected once at the start of the user experience.
 * All search, map, and vibe flows scope to the selected metro.
 *
 * Hydration: read localStorage synchronously at module init so the persisted
 * metro is available on the very first render. Without this, Zustand's
 * default async rehydration fires after the first render — components that
 * call performSearch on mount would race the rehydration and send the
 * request with no city, returning zero results. Round 3 of post-deploy
 * fixes patched this with a `selectedMetro` gate in SearchPage; this PR
 * makes the gate unnecessary by ensuring the value is always present on
 * the first render for returning users.
 *
 * First-visit users still see `selectedMetro === null`, which App.tsx uses
 * to render the MetroPicker onboarding gate. We preserve that signal by
 * returning `null` (not 'Austin') when nothing is stored.
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { safeLocal } from '@/utils/safeStorage';

export interface MetroConfig {
  key: string;
  label: string;
  metro_name: string;
  state: string;
  center: { lat: number; lng: number };
  default_zoom: number;
  tagline: string;
  apartment_count: number;
}

/** Static metro centers — always available without API call */
const METRO_CENTERS: Record<string, { center: { lat: number; lng: number }; zoom: number; label: string }> = {
  Austin: { center: { lat: 30.2672, lng: -97.7431 }, zoom: 13, label: 'Austin' },
  Dallas: { center: { lat: 32.7767, lng: -96.7970 }, zoom: 11, label: 'Dallas / Fort Worth' },
  Houston: { center: { lat: 29.7604, lng: -95.3698 }, zoom: 11, label: 'Houston' },
  'San Antonio': { center: { lat: 29.4252, lng: -98.4946 }, zoom: 11, label: 'San Antonio' },
};

interface MetroState {
  selectedMetro: string | null;
  metros: MetroConfig[];
  metrosLoaded: boolean;
  setSelectedMetro: (metro: string) => void;
  setMetros: (metros: MetroConfig[]) => void;
  getActiveMetro: () => MetroConfig | null;
  getMetroCenter: () => { lat: number; lng: number };
  getMetroZoom: () => number;
  getMetroLabel: () => string;
  clearMetro: () => void;
}

/**
 * Read the persisted metro synchronously at module init. Returns `null`
 * (preserving the first-visit MetroPicker gate) if nothing is stored or
 * the read fails. Mirrors the shape Zustand persist writes:
 *   { state: { selectedMetro: 'Austin' }, version: 0 }
 */
function readPersistedMetroSync(): string | null {
  try {
    const raw = safeLocal.get('taco-metro');
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { state?: { selectedMetro?: string | null } };
    return parsed?.state?.selectedMetro ?? null;
  } catch {
    return null;
  }
}

const INITIAL_SELECTED_METRO = readPersistedMetroSync();

export const useMetroStore = create<MetroState>()(
  persist(
    (set, get) => ({
      selectedMetro: INITIAL_SELECTED_METRO,
      metros: [],
      metrosLoaded: false,

      setSelectedMetro: (metro: string) => set({ selectedMetro: metro }),

      setMetros: (metros: MetroConfig[]) => set({ metros, metrosLoaded: true }),

      getActiveMetro: () => {
        const { selectedMetro, metros } = get();
        if (!selectedMetro) return null;
        return metros.find((m) => m.key === selectedMetro) || null;
      },

      /** Always returns a center — uses static data if API metros not loaded */
      getMetroCenter: () => {
        const { selectedMetro } = get();
        const config = METRO_CENTERS[selectedMetro || 'Austin'];
        return config?.center || METRO_CENTERS.Austin.center;
      },

      /** Always returns a zoom level */
      getMetroZoom: () => {
        const { selectedMetro } = get();
        const config = METRO_CENTERS[selectedMetro || 'Austin'];
        return config?.zoom || 13;
      },

      /** Always returns a label */
      getMetroLabel: () => {
        const { selectedMetro } = get();
        const config = METRO_CENTERS[selectedMetro || 'Austin'];
        return config?.label || 'Austin';
      },

      clearMetro: () => set({ selectedMetro: null }),
    }),
    {
      name: 'taco-metro',
      partialize: (state) => ({ selectedMetro: state.selectedMetro }),
      // skipHydration: don't let persist's async rehydration overwrite the
      // synchronous read above. We still write to storage on every change
      // (the persist middleware's set hook), so user selections persist.
      skipHydration: true,
    }
  )
);
